import { Component, OnInit, inject, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DynamicFormService } from '../../services/dynamic-form.service';
import {
  DynamicFormConfig,
  DynamicFormStep,
  DynamicFormField,
} from '../../interfaces/dynamic-form';
import { DynamicFieldComponent } from './components/dynamic-field/dynamic-field.component';
import { DynamicFileUploadComponent } from './components/dynamic-file-upload/dynamic-file-upload.component';
import { DynamicPrivacyDialogComponent } from './components/dynamic-privacy-dialog/dynamic-privacy-dialog.component';
import { DynamicPriceSummaryComponent } from './components/dynamic-price-summary/dynamic-price-summary.component';
import { DynamicProductCardsComponent, ProductPriceInfo } from './components/dynamic-product-cards/dynamic-product-cards.component';

@Component({
  selector: 'app-iscrizione-dinamica',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DynamicFieldComponent,
    DynamicFileUploadComponent,
    DynamicPrivacyDialogComponent,
    DynamicPriceSummaryComponent,
    DynamicProductCardsComponent,
  ],
  templateUrl: './iscrizione-dinamica.component.html',
  styleUrl: './iscrizione-dinamica.component.scss',
})
export class IscrizioneDinamicaComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private formService = inject(DynamicFormService);
  private el = inject(ElementRef);

  @ViewChild('formCard') formCardRef!: ElementRef<HTMLElement>;

  formConfig: DynamicFormConfig | null = null;
  eventoInfo: {
    id: string;
    titolo: string;
    slug: string;
    iscrizioniAperte: boolean;
  } | null = null;

  loading = true;
  errorMessage = '';
  currentStep = 0;
  formData: Record<string, unknown> = {};
  allegati: { nomeCampo: string; documentoId: string }[] = [];

  privacyAccepted = false;
  showPrivacyDialog = false;
  privacyHtml = '';

  submitting = false;
  submitError = '';
  isSuccess = false;
  successMessage = '';
  paymentInstructionsHtml = '';
  descrizioneHtml = '';
  sfondoUrl = '';

  // Track product prices by field name (both acconto and full price)
  productPrices: Record<string, ProductPriceInfo> = {};

  ngOnInit(): void {
    const year = this.route.snapshot.paramMap.get('year');
    const slug = this.route.snapshot.paramMap.get('slug');

    if (!year || !slug) {
      this.loading = false;
      this.errorMessage = "Parametri mancanti nell'URL.";
      return;
    }

    const eventoSlug = `${year}/${slug}`;
    this.formService.getFormConfig(eventoSlug).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success && response.form && response.evento) {
          this.formConfig = response.form;
          this.eventoInfo = response.evento;
          this.applyTheme();
          this.initPrivacyHtml();
          this.initDescrizioneHtml();
        } else {
          this.errorMessage = response.error || 'Form non disponibile.';
        }
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Errore nel caricamento del form.';
      },
    });
  }

  get currentStepConfig(): DynamicFormStep | null {
    return this.formConfig?.steps?.[this.currentStep] || null;
  }

  get isLastStep(): boolean {
    if (!this.formConfig) return false;
    return this.currentStep === this.formConfig.steps.length - 1;
  }

  get submitButtonLabel(): string {
    const tipo = this.formConfig?.pagamento?.tipo;
    if (tipo === 'stripe') return 'Procedi al pagamento';
    if (tipo === 'manuale') return 'Invia iscrizione';
    return 'Invia iscrizione';
  }

  get totalQuotaTotale(): number {
    return Object.values(this.productPrices).reduce((sum, p) => sum + p.quotaTotale, 0);
  }

  get totalAcconto(): number {
    return Object.values(this.productPrices).reduce((sum, p) => sum + p.acconto, 0);
  }

  get calculatedPrice(): number {
    return this.totalQuotaTotale;
  }

  get priceBreakdown(): { label: string; amount: number }[] {
    if (!this.formConfig) return [];
    const items: { label: string; amount: number }[] = [];

    for (const step of this.formConfig.steps) {
      for (const campo of step.campi) {
        if (campo.blockType !== 'campo-prodotti-stripe') continue;
        const info = this.productPrices[campo.nome];
        if (info && info.quotaTotale > 0) {
          items.push({ label: campo.etichetta, amount: info.quotaTotale });
        }
      }
    }

    return items;
  }

  get hasAcconto(): boolean {
    return this.totalAcconto > 0 && this.totalAcconto < this.totalQuotaTotale;
  }

  // --- Visibility ---

  isFieldVisible(field: DynamicFormField): boolean {
    if (!field.condizione?.attiva) return true;
    return this.evaluateCondition(field.condizione);
  }

  // --- Validation ---

  isCurrentStepValid(): boolean {
    const step = this.currentStepConfig;
    if (!step) return false;

    for (const field of step.campi) {
      if (!field.obbligatorio) continue;
      if (!this.isFieldVisible(field)) continue;
      if (field.blockType === 'campo-file') {
        const hasFile = this.allegati.some((a) => a.nomeCampo === field.nome);
        if (!hasFile) return false;
        continue;
      }
      const value = this.formData[field.nome];
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
    }
    return true;
  }

  isFormValid(): boolean {
    if (!this.formConfig) return false;

    // Check all steps
    for (const step of this.formConfig.steps) {
      for (const field of step.campi) {
        if (!field.obbligatorio) continue;
        if (!this.isFieldVisible(field)) continue;
        if (field.blockType === 'campo-file') {
          if (!this.allegati.some((a) => a.nomeCampo === field.nome))
            return false;
          continue;
        }
        const value = this.formData[field.nome];
        if (value === undefined || value === null || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
      }
    }

    // Check privacy
    if (this.formConfig.privacy?.mostraDialogPrivacy && !this.privacyAccepted) {
      return false;
    }

    return true;
  }

  // --- Navigation ---

  nextStep(): void {
    if (this.isCurrentStepValid() && !this.isLastStep) {
      this.currentStep++;
      this.scrollToForm();
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.scrollToForm();
    }
  }

  private scrollToForm(): void {
    setTimeout(() => {
      this.formCardRef?.nativeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  // --- Field changes ---

  onFieldChange(nome: string, value: unknown): void {
    this.formData[nome] = value;
    this.clearHiddenDependents(nome);
  }

  /** Clear values of fields that depend on `changedField` and are now hidden */
  private clearHiddenDependents(changedField: string): void {
    if (!this.formConfig) return;
    for (const step of this.formConfig.steps) {
      for (const field of step.campi) {
        if (
          field.condizione?.attiva &&
          field.condizione.campo === changedField &&
          !this.isFieldVisible(field)
        ) {
          if (this.formData[field.nome] !== undefined) {
            delete this.formData[field.nome];
          }
          if (this.productPrices[field.nome]) {
            delete this.productPrices[field.nome];
          }
        }
      }
    }
  }

  onFileUploaded(event: { nomeCampo: string; documentoId: string }): void {
    // Remove existing file for this field if any
    this.allegati = this.allegati.filter(
      (a) => a.nomeCampo !== event.nomeCampo,
    );
    this.allegati.push(event);
  }

  onFileRemoved(nomeCampo: string): void {
    this.allegati = this.allegati.filter((a) => a.nomeCampo !== nomeCampo);
  }

  onProductPriceChange(fieldName: string, priceInfo: ProductPriceInfo): void {
    this.productPrices[fieldName] = priceInfo;
  }

  // --- Privacy ---

  onPrivacyAccepted(): void {
    this.privacyAccepted = true;
    this.showPrivacyDialog = false;
  }

  private applyTheme(): void {
    if (!this.formConfig?.tema) return;
    const tema = this.formConfig.tema;
    const host = this.el.nativeElement as HTMLElement;

    if (tema.colorePrincipale) {
      host.style.setProperty('--accent', tema.colorePrincipale);
    }

    if (tema.temaBase === 'chiaro') {
      host.classList.add('tema-chiaro');
    }

    if (tema.sfondoImmagine) {
      const img = tema.sfondoImmagine;
      this.sfondoUrl = typeof img === 'string' ? img : img.url;
    }
  }

  private initDescrizioneHtml(): void {
    if (!this.formConfig?.descrizione) return;
    const desc = this.formConfig.descrizione;
    if (typeof desc === 'string') {
      this.descrizioneHtml = desc;
    } else {
      this.descrizioneHtml = this.lexicalToHtml(desc);
    }
  }

  private initPrivacyHtml(): void {
    if (!this.formConfig?.privacy?.testoPrivacy) return;
    // Rich text from Payload comes as Lexical JSON, we'll render a simple fallback
    const privacy = this.formConfig.privacy.testoPrivacy;
    if (typeof privacy === 'string') {
      this.privacyHtml = privacy;
    } else {
      // Convert Lexical JSON to HTML
      this.privacyHtml = this.lexicalToHtml(privacy);
    }
  }

  private lexicalToHtml(node: unknown): string {
    if (!node || typeof node !== 'object') return '';
    const obj = node as Record<string, unknown>;

    if (obj['root']) return this.lexicalToHtml(obj['root']);

    const type = obj['type'] as string;
    const children = Array.isArray(obj['children'])
      ? (obj['children'] as unknown[]).map((c) => this.lexicalToHtml(c)).join('')
      : '';

    if (type === 'text') {
      let text = obj['text'] as string ?? '';
      const format = (obj['format'] as number) ?? 0;
      if (format & 1) text = `<strong>${text}</strong>`;
      if (format & 2) text = `<em>${text}</em>`;
      if (format & 8) text = `<u>${text}</u>`;
      if (format & 16) text = `<s>${text}</s>`;
      return text;
    }
    if (type === 'linebreak') return '<br>';
    if (type === 'paragraph') return `<p>${children}</p>`;
    if (type === 'heading') {
      const tag = (obj['tag'] as string) ?? 'h2';
      return `<${tag}>${children}</${tag}>`;
    }
    if (type === 'list') {
      const tag = (obj['listType'] as string) === 'number' ? 'ol' : 'ul';
      return `<${tag}>${children}</${tag}>`;
    }
    if (type === 'listitem') return `<li>${children}</li>`;
    if (type === 'link') {
      const url = (obj['url'] as string) ?? '#';
      return `<a href="${url}" target="_blank" rel="noopener">${children}</a>`;
    }

    return children;
  }

  // --- Submission ---

  onSubmit(): void {
    if (!this.isFormValid() || !this.formConfig || !this.eventoInfo) return;

    this.submitting = true;
    this.submitError = '';

    this.formService
      .submitRegistration(
        this.formConfig.id,
        this.eventoInfo.id,
        this.formData,
        this.allegati.length > 0 ? this.allegati : undefined,
      )
      .subscribe({
        next: async (response) => {
          if (response.success) {
            if (response.requiresPayment && response.sessionUrl) {
              // Redirect to Stripe Checkout via URL (works regardless of key config)
              window.location.href = response.sessionUrl;
              return;
            } else {
              // Success without payment or manual payment
              this.submitting = false;
              this.isSuccess = true;
              this.successMessage =
                response.messaggioSuccesso ||
                this.formConfig?.messaggioSuccesso ||
                'Iscrizione completata con successo!';

              // Show manual payment instructions if applicable
              if (
                this.formConfig?.pagamento?.tipo === 'manuale' &&
                this.formConfig.pagamento.istruzioniPagamento
              ) {
                const istruzioni =
                  this.formConfig.pagamento.istruzioniPagamento;
                if (typeof istruzioni === 'string') {
                  this.paymentInstructionsHtml = istruzioni;
                } else {
                  this.paymentInstructionsHtml =
                    this.lexicalToHtml(istruzioni);
                }
              }
            }
          } else {
            this.submitting = false;
            this.submitError =
              response.error || "Errore nell'invio dell'iscrizione.";
          }
        },
        error: (err) => {
          this.submitting = false;
          this.submitError =
            err?.error?.error || "Errore nell'invio. Riprova più tardi.";
        },
      });
  }

  private evaluateCondition(condition: {
    campo?: string;
    operatore?: string;
    valore?: string;
  }): boolean {
    if (!condition.campo || !condition.operatore) return true;

    const fieldValue = this.formData[condition.campo];
    const condValue = condition.valore;

    switch (condition.operatore) {
      case 'equals':
        return String(fieldValue) === condValue;
      case 'not_equals':
        return String(fieldValue) !== condValue;
      case 'contains':
        return String(fieldValue || '').includes(condValue || '');
      case 'greater_than':
        return Number(fieldValue) > Number(condValue);
      case 'less_than':
        return Number(fieldValue) < Number(condValue);
      case 'is_not_empty':
        return (
          fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
        );
      case 'is_empty':
        return (
          fieldValue === undefined || fieldValue === null || fieldValue === ''
        );
      default:
        return true;
    }
  }
}
